import { THEME } from '@common/constants/theme.constant';
import { Field, IFieldProps } from '@common/general-ui/field/field.component';
import { Readonly } from '@common/general-ui/readonly.component';
import { ISelectOption } from '@common/general-ui/select/select.component';
import { css as emotionCss } from '@emotion/css';
import { SerializedStyles } from '@emotion/react';
import { Select as AntdSelect, SelectProps } from 'antd';
import { RefSelectProps } from 'antd/lib/select';
import React from 'react';
import { css } from 'twin.macro';

const { Option } = AntdSelect;

export type IMultiSelectChangeHandler = (e: { value: string[]; name: string }) => void;
export const MultiSelectOption = Option;
export interface IRenderOptionEventsArgs extends ISelectOption {
    disabled: boolean;
    ['data-parent-testid']: string;
}
export interface IMultiSelectProps<TName = string> extends IFieldProps<string[], TName> {
    maxCount?: number;
    className?: string;
    onChange?: IMultiSelectChangeHandler;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    options?: ISelectOption[];
    renderMultiSelectOption?: (e: IRenderOptionEventsArgs) => JSX.Element;
}

export const MultiSelect = React.forwardRef<SelectProps<string>, IMultiSelectProps>(
    (props: IMultiSelectProps, ref): JSX.Element => {
        const { onChange, onBlur, name, value, defaultValue, errorMessage, placeholder, maxCount, options = [] } = props;

        const renderOptions = (): JSX.Element[] => {
            return options.map((option) => {
                const disabled = !!maxCount && (value?.length || 0) >= maxCount && !value?.includes(option.value as string);
                return (
                    <React.Fragment key={option.value}>
                        {props.renderMultiSelectOption ? (
                            props.renderMultiSelectOption({
                                ...option,
                                'data-parent-testid': 'field-' + props.name,
                                disabled,
                            })
                        ) : (
                            <MultiSelectOption
                                data-parent-testid={'field-' + props.name}
                                value={option.value}
                                disabled={disabled}
                            >
                                {option.label}
                            </MultiSelectOption>
                        )}
                    </React.Fragment>
                );
            });
        };

        const handleChange = (value: string[]): void => {
            onChange && onChange({ value, name });
        };

        return (
            <Field {...props} errorMessage={errorMessage}>
                <div tw="relative">
                    {props.readOnly && <Readonly />}

                    <AntdSelect
                        showArrow={true}
                        ref={ref as unknown as React.Ref<RefSelectProps>}
                        css={styles.root(!!props.readOnly)}
                        dropdownClassName={styles.dropDown}
                        mode="multiple"
                        allowClear
                        tabIndex={props.readOnly ? -1 : 0}
                        showSearch={!props.readOnly}
                        placeholder={placeholder}
                        defaultValue={defaultValue}
                        value={value}
                        onChange={handleChange}
                        onBlur={onBlur}
                        dropdownRender={(prev): JSX.Element => (props.readOnly ? <></> : prev)}
                    >
                        {renderOptions()}
                    </AntdSelect>
                </div>
            </Field>
        );
    },
);

const styles = {
    root: (readOnly: boolean): (SerializedStyles | boolean)[] => [
        css`
            display: block;
            && .ant-select-selector {
                display: flex;
                height: auto;
                min-height: 34px;
                border: 1px solid ${THEME.GRAY};
                border-radius: 4px;
                font-weight: 400;
                color: ${THEME.BLACK};
                width: 100%;
                transition: border-color ease-in-out 0.15s, box-shadow ease-in-out 0.15s;
            }
            && .ant-select-selection-item {
                background-color: white;
                color: ${THEME.BLACK};
                border: 1px solid ${THEME.GRAY_200};
            }
            &&.ant-select-disabled {
                && .ant-select-selection-item {
                    background-color: ${THEME.DISABLED_BACKGROUND};
                    color: ${THEME.GRAY};
                }
            }
            && .ant-select-selection-item-content {
                padding-top: 2px;
            }
            && .ant-select-selection-item-remove {
                padding-top: 2px;
                font-weight: bold;
                color: ${THEME.BLACK};
            }

            &.ant-select-focused {
                && .ant-select-selector {
                    border: 1px solid ${THEME.GREEN};
                    outline-color: transparent;
                    outline-style: none;
                    ${THEME.BOX_SHADOW_GREEN}
                }
            }
            && .anticon-close {
                position: relative;
                top: -2px;
            }
        `,
        readOnly &&
            css`
                && .ant-select-selector {
                    // border-bottom: 1px solid ${THEME.READONLY_COLOR};
                }
            `,
    ],

    dropDown: emotionCss`
		&& .ant-select-item-option-selected {
			background-color: #fff ;
		}  
		&& .ant-select-item-option-active {
            background-color: ${THEME.GREEN};
            color: white;
        }
		.divider {
			padding:8px 12px;
			width: 100%;
			height: auto;
			line-height: 1px;
			min-height: 1px;
		}

		.divider div{
			border-bottom: 1px solid ${THEME.GRAY};
		}
			
    `,
};

// Don't use component - problem with child loop (antd)
export const renderMultiSelectDividerOption = (): JSX.Element => {
    return (
        <Option value="" disabled={true} className="divider">
            {/* Don't remove this div */}
            <div>&nbsp;</div>
        </Option>
    );
};
